import React from 'react';
import {
  formatFileSize,
  getFileTypeCategory,
} from '../../../../utils/fileUtils';

import { useTranslation } from 'react-i18next';

interface MessageFileItemProps {
  fileName: string;
  mimeType?: string;
  sizeBytes?: number;
  // Optional preview URL for images
  previewUrl?: string;
  onDownload: () => void;
  showDownload?: boolean;
}

const getExtensionLabel = (fileName: string, mime?: string): string => {
  const extFromName =
    fileName && fileName.includes('.') ? fileName.split('.').pop() || '' : '';
  let ext = (extFromName || '').toUpperCase();
  if (!ext && mime) {
    if (mime.includes('pdf')) {
      ext = 'PDF';
    } else if (mime.includes('json')) {
      ext = 'JSON';
    } else if (mime.includes('csv')) {
      ext = 'CSV';
    } else if (mime.includes('yaml') || mime.includes('yml')) {
      ext = 'YAML';
    } else if (mime.includes('xml')) {
      ext = 'XML';
    } else if (mime.includes('plain')) {
      ext = 'TXT';
    } else if (mime.includes('sheet')) {
      ext = 'XLSX';
    } else if (mime.includes('excel')) {
      ext = 'XLS';
    } else if (mime.startsWith('image/')) {
      ext = mime.replace('image/', '').toUpperCase();
    }
  }
  return (ext || 'FILE').slice(0, 5);
};

const getIconBgClass = (ext: string): string => {
  switch (ext) {
    case 'PDF':
      return 'bg-blue-50 dark:bg-blue-950';
    case 'CSV':
      return 'bg-emerald-50 dark:bg-emerald-950';
    case 'JSON':
      return 'bg-amber-50 dark:bg-amber-950';
    case 'YAML':
      return 'bg-violet-50 dark:bg-violet-950';
    case 'XML':
      return 'bg-indigo-50 dark:bg-indigo-950';
    case 'TXT':
      return 'bg-slate-500';
    case 'XLS':
    case 'XLSX':
      return 'bg-green-50 dark:bg-green-950';
    default:
      return 'bg-blue-50 dark:bg-blue-950';
  }
};

export const MessageFileItem: React.FC<MessageFileItemProps> = ({
  fileName,
  mimeType,
  sizeBytes,
  previewUrl,
  onDownload,
  showDownload = true,
}) => {
  const { t } = useTranslation('pleri/chat');
  const fileType = getFileTypeCategory(mimeType || '');
  const extLabel = getExtensionLabel(fileName, mimeType);

  // Image style (square thumbnail)
  if (fileType === 'image' && previewUrl) {
    return (
      <div className="inline-flex flex-col items-start">
        <div
          className="group relative h-14 w-14 flex-shrink-0 overflow-hidden rounded-2xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900"
          title={fileName}
        >
          <img
            src={previewUrl}
            alt={fileName}
            className="h-full w-full object-cover"
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
          <span className="sr-only">
            {fileName}
            {sizeBytes ? ` • ${formatFileSize(sizeBytes)}` : ''}
          </span>
        </div>
        {showDownload && (
          <button
            type="button"
            onClick={onDownload}
            className="mt-2 inline-flex items-center gap-1 rounded bg-gray-100 px-2 py-1 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-600"
          >
            {t('download')}
          </button>
        )}
      </div>
    );
  }

  // Document style (rectangle chip)
  return (
    <div className="w-full max-w-full items-center gap-3 rounded-2xl border border-gray-200 bg-white px-4 py-3 pr-3 shadow-sm sm:inline-flex sm:max-w-[24rem] dark:border-gray-700 dark:bg-gray-900">
      <div
        className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-2xl ${getIconBgClass(extLabel)} text-white`}
      >
        <svg
          className="h-5 w-5"
          viewBox="0 0 24 24"
          fill="currentColor"
          aria-hidden
        >
          <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6z" />
          <path d="M14 2v6h6" className="opacity-90" />
        </svg>
      </div>
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">
          {fileName}
        </div>
        <div className="text-xs text-gray-500 dark:text-gray-400">
          {extLabel}
          {sizeBytes ? ` • ${formatFileSize(sizeBytes)}` : ''}
        </div>
      </div>
      {showDownload && (
        <button
          type="button"
          onClick={onDownload}
          className="inline-flex items-center gap-1 rounded bg-gray-100 px-2 py-1 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-600"
        >
          {t('download')}
        </button>
      )}
    </div>
  );
};
